import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: {
      id: string;
      email: string;
      name: string;
      role: string;
      isActive: boolean;
    };
  };
  errors?: string[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AssetAuthService {
  private readonly API_URL = environment.assetApiUrl;
  private readonly TOKEN_KEY = 'asset_auth_token';
  private readonly USER_KEY = 'asset_user_data';

  private currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  private isLoggedInSubject = new BehaviorSubject<boolean>(this.hasValidToken());

  public currentUser$ = this.currentUserSubject.asObservable();
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check token validity on service initialization
    this.validateToken();
  }

  /**
   * Login with email and password
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}auth/login`, credentials)
      .pipe(
        tap(response => {
          if (response.success && response.data) {
            this.setSession(response.data.token, response.data.user);
          }
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Logout and clear session
   */
  logout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  /**
   * Simple authentication setup - for demo purposes
   */
  setDemoAuthentication(role: 'ADMIN' | 'MANAGER' | 'USER' = 'ADMIN'): void {
    // Create a demo user
    const user: User = {
      id: '1',
      name: 'Demo User',
      email: 'demo@test.com',
      role: role,
      isActive: true
    };

    // Set authentication state
    this.currentUserSubject.next(user);
    this.isLoggedInSubject.next(true);

    // Store demo token (not a real JWT, just for demo)
    const demoToken = 'demo-token-' + Date.now();
    localStorage.setItem(this.TOKEN_KEY, demoToken);

    console.log('Demo authentication set for role:', role);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get JWT token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.role === role : false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  }

  /**
   * Get authorization headers for API calls
   */
  getAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    if (token) {
      return new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  /**
   * Refresh user data from API
   */
  refreshUserData(): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.get(`${this.API_URL}auth/me`, { headers })
      .pipe(
        tap((response: any) => {
          if (response.success && response.data) {
            this.updateUser(response.data);
          }
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Set authentication session
   */
  private setSession(token: string, user: User): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
    this.isLoggedInSubject.next(true);
  }

  /**
   * Clear authentication session
   */
  private clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.currentUserSubject.next(null);
    this.isLoggedInSubject.next(false);
  }

  /**
   * Update user data
   */
  private updateUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /**
   * Get user from localStorage
   */
  private getUserFromStorage(): User | null {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Check if token exists and is valid
   */
  private hasValidToken(): boolean {
    const token = this.getToken();
    if (token) {
      // Handle demo token
      if (token.startsWith('demo-token-')) {
        return true;
      }

      try {
        // Simple JWT token structure check
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        // Check if token is expired
        if (payload.exp && payload.exp < currentTime) {
          this.clearSession();
          return false;
        }
        
        return true;
      } catch (error) {
        console.error('Invalid token format:', error);
        this.clearSession();
        return false;
      }
    }

    // Fallback: Check if there's a legacy session with assetmanagement role
    const loginData = localStorage.getItem('loginData');
    if (loginData) {
      try {
        const roles = JSON.parse(loginData);
        if (roles.includes('assetmanagement')) {
          // Create a mock user for compatibility
          this.currentUserSubject.next({
            id: '1',
            name: 'Test User',
            email: 'test@test.com',
            role: 'ADMIN',
            isActive: true
          });
          this.isLoggedInSubject.next(true);
          return true;
        }
      } catch (error) {
        console.error('Legacy session check error:', error);
      }
    }

    return false;
  }

  /**
   * Validate token with server
   */
  private validateToken(): void {
    if (this.hasValidToken()) {
      // Optionally validate with server
      // this.refreshUserData().subscribe({
      //   error: () => this.clearSession()
      // });
    }
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      // Server-side error
      if (error.status === 401) {
        errorMessage = 'Invalid credentials. Please check your email and password.';
      } else if (error.status === 403) {
        errorMessage = 'Access denied. You do not have permission to perform this action.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else {
        errorMessage = `Server Error: ${error.status} ${error.statusText}`;
      }
    }

    console.error('Auth Service Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}